import { useState, useCallback } from 'react';
import { Box, Grid, Typography, Alert, Button, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useNotify } from 'react-admin';
import type {
  Grid2DFormProps,
  Grid2DConfig,
  CriteriaWithLabel,
  Grid2DValidationResult,
} from './types';
import { Grid2DAxisConfig } from './Grid2DAxisConfig';
import { Grid2DPreview } from './Grid2DPreview';
import { apiPost, ApiClientError } from '../../utils/apiClient';
import { conditionsToDsl } from '../CriteriaBuilder';

const createEmptyCriteria = (): CriteriaWithLabel => ({
  label: '',
  dsl: '',
  conditions: [],
});

const createDefaultConfig = (xSize = 3, ySize = 3): Grid2DConfig => ({
  gameType: 'grid',
  dimensions: { x: xSize, y: ySize },
  criteria: {
    x: Array.from({ length: xSize }, () => createEmptyCriteria()),
    y: Array.from({ length: ySize }, () => createEmptyCriteria()),
  },
});

export const Grid2DConfigForm = ({
  entityType,
  category,
  value,
  onChange,
  disabled = false,
}: Grid2DFormProps) => {
  const notify = useNotify();
  const [validationResult, setValidationResult] = useState<Grid2DValidationResult | undefined>();
  const [validating, setValidating] = useState(false);
  const [computing, setComputing] = useState(false);

  // Initialize with default config if empty
  const config = value?.gameType === 'grid' ? value : createDefaultConfig();

  const handleAxisChange = useCallback(
    (axis: 'x' | 'y', criteria: CriteriaWithLabel[]) => {
      // Only clear validation if conditions actually changed (not just DSL text)
      const oldConditions = config.criteria[axis].map((c) => JSON.stringify(c.conditions));
      const newConditions = criteria.map((c) => JSON.stringify(c.conditions));
      const conditionsChanged =
        oldConditions.length !== newConditions.length ||
        oldConditions.some((c, i) => c !== newConditions[i]);

      // Derive dimensions from criteria array lengths to keep them in sync
      const newCriteria = { ...config.criteria, [axis]: criteria };
      const updated = {
        ...config,
        dimensions: {
          x: newCriteria.x.length,
          y: newCriteria.y.length,
        },
        criteria: newCriteria,
      };
      onChange(updated);
      if (conditionsChanged) {
        setValidationResult(undefined);
      }
    },
    [config, onChange]
  );

  const getDsl = (c: CriteriaWithLabel): string => c.dsl || conditionsToDsl(c.conditions);

  const handleValidate = useCallback(async () => {
    const allCriteria = [...config.criteria.x, ...config.criteria.y];
    const emptyCriteria = allCriteria.filter((c) => c.conditions.length === 0);
    if (emptyCriteria.length > 0) {
      notify(
        `${emptyCriteria.length} criteria have no conditions. Please define conditions for all criteria.`,
        { type: 'warning' }
      );
      return;
    }

    setValidating(true);

    try {
      const result = await apiPost<Grid2DValidationResult>(
        '/admin/api/game-configs/2d-grid/validate',
        {
          entityType,
          category,
          dimensions: config.dimensions,
          criteria: {
            x: config.criteria.x.map(getDsl),
            y: config.criteria.y.map(getDsl),
          },
        }
      );

      setValidationResult(result);

      if (result.valid) {
        notify('All cells have valid answers!', { type: 'success' });
      } else {
        notify(`${result.errorCells} cells have no valid answers`, { type: 'warning' });
      }
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Validation failed';
      notify(message, { type: 'error' });
    } finally {
      setValidating(false);
    }
  }, [config, entityType, category, notify]);

  const handleComputeAnswers = useCallback(async () => {
    if (!validationResult?.valid) {
      notify('Please validate all cells first', { type: 'warning' });
      return;
    }

    setComputing(true);

    try {
      const result = await apiPost<{ answers: Record<string, string[]> }>(
        '/admin/api/game-configs/2d-grid/compute-answers',
        {
          entityType,
          category,
          dimensions: config.dimensions,
          criteria: {
            x: config.criteria.x.map(getDsl),
            y: config.criteria.y.map(getDsl),
          },
        }
      );

      const updated: Grid2DConfig = {
        ...config,
        answers: result.answers,
      };
      onChange(updated);

      notify('Answers computed and saved!', { type: 'success' });
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to compute answers';
      notify(message, { type: 'error' });
    } finally {
      setComputing(false);
    }
  }, [config, entityType, category, validationResult, onChange, notify]);

  const isConfigComplete =
    config.criteria.x.every((c) => c.conditions.length > 0) &&
    config.criteria.y.every((c) => c.conditions.length > 0);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Configure an {config.dimensions.x}x{config.dimensions.y} grid game. Define criteria for
        each axis (X=rows, Y=columns). Each cell requires entities that match both criteria at once.
        Use the + / - buttons to adjust axis size (2-6).
      </Alert>

      <Grid container spacing={3}>
        {/* Axis Configuration */}
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" gutterBottom>
            Axis Configuration
          </Typography>

          <Grid2DAxisConfig
            axis="x"
            axisLabel="X (Row)"
            entityType={entityType}
            criteria={config.criteria.x}
            onChange={(criteria) => handleAxisChange('x', criteria)}
            disabled={disabled}
          />

          <Grid2DAxisConfig
            axis="y"
            axisLabel="Y (Column)"
            entityType={entityType}
            criteria={config.criteria.y}
            onChange={(criteria) => handleAxisChange('y', criteria)}
            disabled={disabled}
          />
        </Grid>

        {/* Preview */}
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" gutterBottom>
            Grid Preview & Validation
          </Typography>

          {!isConfigComplete ? (
            <Alert severity="warning">
              Please define conditions for all criteria before previewing.
            </Alert>
          ) : (
            <>
              <Grid2DPreview
                entityType={entityType}
                category={category}
                config={config}
                validationResult={validationResult}
                onValidate={handleValidate}
                loading={validating}
              />

              {validationResult?.valid && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={computing ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleComputeAnswers}
                    disabled={computing || disabled}
                    fullWidth
                  >
                    Compute & Save Answers
                  </Button>
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
